import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faRotate,
  faSearch,
  faTriangleExclamation,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { platformUserService } from '@/services/platform-user.service';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import {
  PLATFORM_ROLES,
  type CreatePlatformUserRequest,
  type PlatformRole,
  type PlatformUserSummary,
  type UpdatePlatformUserIdentityRequest,
} from '@/types/platform-user.types';
import { getApiErrorMessage } from '@/lib/api-error';
import { canPlatform } from '@/lib/platform-permissions';
import { toast } from '@/lib/swal';
import { PlatformUserDetailDrawer } from './PlatformUserDetailDrawer';
import { PlatformUserActionDialog, type UserAction } from './PlatformUserActionDialog';
import { PlatformUserIdentityDialog } from './PlatformUserIdentityDialog';
import { PlatformUserCreateDialog } from './PlatformUserCreateDialog';
import { PlatformRoleReferenceCard } from './PlatformRoleReferenceCard';
import {
  RoleBadge,
  StatusBadge,
  formatLastSignIn,
  formatRelative,
  formatUserDateTime,
  roleLabel,
} from './user-presentation';

/**
 * Phase 12.6-4 — the Platform Users list and detail drawer.
 *
 * Design of record: `PLATFORM_USERS_DESIGN.md` v1.0 §5.1–§5.6, §7.
 * Backend contract verified by 12.6-2; API client built in 12.6-3B.
 *
 * ── What this replaced ──────────────────────────────────────────────────────
 * A 68-line placeholder that contradicted the approved design in four ways: an
 * **"Invite User"** button for a workflow OD-B **declined**; "Coming in Phase
 * 12.3" copy on a Phase 12.6 page; a hardcoded role list claiming Platform
 * Support **"May request impersonation sessions"** when impersonation has no
 * schema, service, route or code; and a `platform-admin` description that, on a
 * page about managing platform users, omitted that `platform-admin` **cannot**
 * manage them. All four are gone.
 *
 * ── Everything that narrows the list is CLIENT-SIDE, deliberately ───────────
 * `GET /platform/users` accepts **no query parameters** and returns a bare,
 * unpaginated array. §5.3 decides search, filter and sort are client-side for
 * this bounded staff table, and records that server-side search would be a new
 * backend requirement. Sending `?search=` would be silently ignored — a search
 * box that quietly lies. So the list is fetched once and narrowed in memory.
 *
 * This is the opposite of Subscriptions (12.4E), which filters server-side
 * because its table is unbounded and paginated. The divergence is the design's,
 * not an oversight.
 *
 * ── No URL state ────────────────────────────────────────────────────────────
 * §7: filters are transient and no deep-link contract is promised, matching
 * Communication. Subscriptions persists filters in the URL (D-17); this page
 * deliberately does not.
 *
 * ── Mutations (12.6-5) ──────────────────────────────────────────────────────
 * Identity edit, role change, activate/deactivate and revoke-sessions are hosted
 * here and rendered in the drawer. Three rules govern how they run:
 *
 *  1. **The dialog owns the typed state; this page owns `submitting` and
 *     `error`.** A rejected submit therefore leaves the dialog mounted with
 *     every typed value intact — a 409 on a duplicate email costs one field, not
 *     three.
 *  2. **The refetch happens only AFTER the dialog closes.** Refetching while a
 *     dialog is open would swap the list for a loading skeleton and unmount the
 *     form under the operator — the defect the Subscriptions work caught.
 *  3. **Re-read, never patch.** `tokenVersion`, `updatedAt` and the effect of
 *     server-side normalisation are all written by the backend; guessing them
 *     here is how a console starts showing something the database does not agree
 *     with.
 *
 * Creation (`POST /platform/users`) is hosted here too, from the header rather
 * than from a user's drawer — it belongs to the page, not to a row. It is the
 * one mutation that must refetch the WHOLE list on success, because the new row
 * does not exist yet and there is nothing to re-read by id; the dialog is closed
 * before that happens, so rule 2 still holds.
 */

type StatusFilter = 'all' | 'active' | 'inactive';
type SortKey = 'displayName' | 'role' | 'status' | 'lastLoginAt' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Deactivated' },
];

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'displayName', label: 'Name' },
  { value: 'role', label: 'Role' },
  { value: 'status', label: 'Status' },
  { value: 'lastLoginAt', label: 'Last sign-in' },
  { value: 'createdAt', label: 'Created' },
];

const COLUMNS = ['User', 'Role', 'Status', 'Last sign-in', 'Created'] as const;

export function PlatformUsersPage() {
  const [users, setUsers] = useState<PlatformUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<PlatformRole | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // §5.3 — "default `displayName` ascending (a staff directory reads
  // alphabetically, not newest-first)".
  const [sortKey, setSortKey] = useState<SortKey>('displayName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const [selectedId, setSelectedId] = useState<string | null>(null);

  /** Own id, for the self-banner. Matched by **id**, never email or username. */
  const currentUserId = usePlatformAuthStore((s) => s.user?.id);
  const currentRole = usePlatformAuthStore((s) => s.user?.role);

  /**
   * The one gate. `platform:platform-users:manage` is owner-only in the frozen
   * matrix (D-041 §5.2) — `canPlatform` fails closed, so an absent or
   * unrecognised role grants nothing.
   *
   * This decides what to DRAW. The backend decides what is ALLOWED, and returns
   * 403 regardless of what this renders.
   */
  const canManage = canPlatform(currentRole, 'platform:platform-users:manage');

  const [action, setAction] = useState<UserAction | null>(null);
  const [editingIdentity, setEditingIdentity] = useState(false);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    platformUserService
      .list()
      .then((rows) => {
        if (cancelled) return;
        setUsers(rows);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // The server's message is preserved rather than replaced with a generic
        // one — a 403 explains itself far better than "Something went wrong".
        setError(getApiErrorMessage(err, 'Platform users could not be loaded.'));
        setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  /**
   * Refetch a single user after a mutation and fold it back into the list.
   *
   * Deliberately NOT the full `list()`: re-running the list would flip `loading`
   * and replace the table with skeletons, which is what unmounts an open dialog.
   * One row is re-read from the server and spliced in, so the rest of the page
   * does not move.
   */
  const refreshUser = useCallback(async (id: string): Promise<void> => {
    try {
      const fresh = await platformUserService.getById(id);
      setUsers((prev) => prev.map((u) => (u.id === fresh.id ? fresh : u)));
    } catch {
      // The mutation already succeeded; a failed re-read is a stale row, not a
      // failed action. Say nothing rather than report a failure that did not
      // happen — Refresh re-reads everything.
    }
  }, []);

  const closeDialogs = useCallback((): void => {
    setAction(null);
    setEditingIdentity(false);
    setCreating(false);
    setActionError('');
  }, []);

  /**
   * Create a platform user.
   *
   * The only mutation that refetches the entire list: a brand-new row cannot be
   * re-read by id because the page does not know its id until the response
   * arrives, and `getById` on a row absent from `users` would splice nothing.
   * `reloadToken` is the page's existing refetch mechanism — the same one the
   * Refresh button uses — so no second data-loading path is introduced.
   *
   * The dialog is closed BEFORE the refetch, so the loading skeleton that
   * `reloadToken` triggers can never unmount an open form.
   */
  const createUser = useCallback(
    async (payload: CreatePlatformUserRequest): Promise<void> => {
      setSubmitting(true);
      setActionError('');
      try {
        const created = await platformUserService.create(payload);

        // Close first — the refetch below flips `loading`.
        closeDialogs();
        // Identity only. The password is never named, echoed or logged.
        toast.success(`${created.displayName} created as ${roleLabel(created.role)}.`);
        setReloadToken((t) => t + 1);
      } catch (err: unknown) {
        // Stays open with every typed value — including the password — intact.
        setActionError(
          getApiErrorMessage(err, 'The user could not be created. Nothing was added.'),
        );
      } finally {
        setSubmitting(false);
      }
    },
    [closeDialogs],
  );

  const runAction = useCallback(
    async (targetRole?: PlatformRole): Promise<void> => {
      const target = users.find((u) => u.id === selectedId);
      if (!target || !action) return;

      setSubmitting(true);
      setActionError('');
      try {
        let message: string;
        switch (action) {
          case 'role':
            if (!targetRole) return;
            await platformUserService.changeRole(target.id, { role: targetRole });
            message = `Role changed to ${roleLabel(targetRole)}. Their sessions were ended.`;
            break;
          case 'deactivate':
            await platformUserService.setActive(target.id, { isActive: false });
            message = 'Account deactivated. Their sessions were ended.';
            break;
          case 'activate':
            await platformUserService.setActive(target.id, { isActive: true });
            // No session claim: activation revokes nothing, because a
            // deactivated account has no live sessions.
            message = 'Account activated. They can sign in again.';
            break;
          case 'revoke-sessions':
            // Returns { message }, NOT a user — nothing is read off it as a row.
            await platformUserService.revokeSessions(target.id);
            message = 'Sessions revoked. They will need to sign in again.';
            break;
        }

        // Close FIRST, then refetch — the refetch must never unmount an open
        // dialog (rule 2).
        closeDialogs();
        toast.success(message);
        await refreshUser(target.id);
      } catch (err: unknown) {
        // Stays open, typed state intact, server message preserved.
        setActionError(
          getApiErrorMessage(err, 'The action could not be completed. Nothing was changed.'),
        );
      } finally {
        setSubmitting(false);
      }
    },
    [users, selectedId, action, closeDialogs, refreshUser],
  );

  const saveIdentity = useCallback(
    async (payload: UpdatePlatformUserIdentityRequest): Promise<void> => {
      const target = users.find((u) => u.id === selectedId);
      if (!target) return;

      setSubmitting(true);
      setActionError('');
      try {
        await platformUserService.updateIdentity(target.id, payload);

        // The session claim is made ONLY for an email change. The response DTO
        // carries no `sessionsRevoked` flag — that value exists only in the
        // audit payload — so the message is derived from what was actually sent,
        // using the same normalisation the server applies.
        const emailChanged = payload.email !== undefined && payload.email !== target.email;

        closeDialogs();
        toast.success(
          emailChanged
            ? 'Identity updated. The email changed, so their sessions were ended.'
            : 'Identity updated.',
        );
        await refreshUser(target.id);
      } catch (err: unknown) {
        setActionError(
          getApiErrorMessage(err, 'The changes could not be saved. Nothing was updated.'),
        );
      } finally {
        setSubmitting(false);
      }
    },
    [users, selectedId, closeDialogs, refreshUser],
  );

  const clearFilters = useCallback((): void => {
    setSearch('');
    setRoleFilter('all');
    setStatusFilter('all');
  }, []);

  const filtersActive = search.trim() !== '' || roleFilter !== 'all' || statusFilter !== 'all';

  const visible = useMemo(() => {
    // §5.3 — search covers exactly `displayName`, `email` and `username`. Not
    // the role: a role has its own filter, and matching "admin" as free text
    // would make that filter unpredictable.
    const needle = search.trim().toLowerCase();

    const filtered = users.filter((u) => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (statusFilter === 'active' && !u.isActive) return false;
      if (statusFilter === 'inactive' && u.isActive) return false;
      if (needle === '') return true;
      return (
        u.displayName.toLowerCase().includes(needle) ||
        u.email.toLowerCase().includes(needle) ||
        (u.username?.toLowerCase().includes(needle) ?? false)
      );
    });

    const direction = sortDirection === 'asc' ? 1 : -1;

    // Sorted on a COPY — mutating `users` in a memo would corrupt the source
    // list and make the next filter pass order-dependent.
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'role':
          // Compares human labels, so the order matches what is on screen
          // rather than the underlying slug.
          return direction * roleLabel(a.role).localeCompare(roleLabel(b.role));
        case 'status':
          // Active first when ascending.
          return direction * (Number(b.isActive) - Number(a.isActive));
        case 'lastLoginAt': {
          // `null` means never signed in. It always sorts LAST regardless of
          // direction: "never" is not "a very old date", and letting it lead the
          // list under one direction would misrepresent it.
          const at = a.lastLoginAt ? Date.parse(a.lastLoginAt) : null;
          const bt = b.lastLoginAt ? Date.parse(b.lastLoginAt) : null;
          if (at === null && bt === null) return 0;
          if (at === null) return 1;
          if (bt === null) return -1;
          return direction * (bt - at);
        }
        case 'createdAt':
          return direction * (Date.parse(b.createdAt) - Date.parse(a.createdAt));
        case 'displayName':
        default:
          return direction * a.displayName.localeCompare(b.displayName);
      }
    });
  }, [users, search, roleFilter, statusFilter, sortKey, sortDirection]);

  const selected = useMemo(
    () =>
      visible.find((u) => u.id === selectedId) ?? users.find((u) => u.id === selectedId) ?? null,
    [visible, users, selectedId],
  );

  /** Matched by id — never by email or username, which are both editable. */
  const isSelfSelected =
    selected !== null && currentUserId !== undefined && selected.id === currentUserId;

  const showEmpty = !loading && !error && users.length === 0;
  const showNoResults = !loading && !error && users.length > 0 && visible.length === 0;

  return (
    <div className="space-y-5">
      {/* ── Header (§5.1) ────────────────────────────────────────────────────
          "[+ New User]" is the primary action, and §5.1 marks it *"visible only
          with platform-users:manage"* — which D-041 §5.2 grants to
          `platform-owner` alone. Hidden, not disabled (§5.5). The hiding is
          presentation; the backend refuses the same POST with 403 regardless. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-foreground">Platform Users</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Manage platform staff — their roles and access
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setReloadToken((t) => t + 1)}
            disabled={loading}
            className="flex h-11 shrink-0 items-center gap-2 rounded-lg border border-border px-3.5 text-sm font-medium text-foreground transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 sm:h-9"
          >
            <FontAwesomeIcon icon={faRotate} className="h-3.5 w-3.5" />
            Refresh
          </button>
          {canManage && (
            <button
              type="button"
              onClick={() => {
                setActionError('');
                setCreating(true);
              }}
              className="flex h-11 shrink-0 items-center gap-2 rounded-lg bg-primary px-3.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring sm:h-9"
            >
              <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
              New user
            </button>
          )}
        </div>
      </div>

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <FontAwesomeIcon
            icon={faSearch}
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            aria-label="Search platform users by name, email or username"
            placeholder="Search by name, email or username…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-11 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-9"
          />
        </div>

        <select
          aria-label="Filter by role"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as PlatformRole | 'all')}
          className="h-11 min-w-0 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-9"
        >
          <option value="all">All roles</option>
          {/* The seven frozen D-041 roles, from the authoritative union. */}
          {PLATFORM_ROLES.map((r) => (
            <option key={r} value={r}>
              {roleLabel(r)}
            </option>
          ))}
        </select>

        <select
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="h-11 min-w-0 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-9"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>

        <select
          aria-label="Sort by"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as SortKey)}
          className="h-11 min-w-0 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-9"
        >
          {SORT_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              Sort: {s.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))}
          aria-label={`Sort direction: ${sortDirection === 'asc' ? 'ascending' : 'descending'}`}
          className="h-11 shrink-0 rounded-lg border border-border px-3.5 text-sm font-medium text-foreground transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring sm:h-9"
        >
          {sortDirection === 'asc' ? 'Asc' : 'Desc'}
        </button>

        {filtersActive && (
          <button
            type="button"
            onClick={clearFilters}
            className="h-11 shrink-0 rounded-lg px-3 text-sm font-medium text-primary transition-colors hover:underline focus:outline-none focus:ring-2 focus:ring-ring sm:h-9"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* ── Error (§5.5) — inline panel with Retry, never a blank page ────── */}
      {error && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3"
        >
          <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4 text-destructive" />
          <p className="min-w-0 flex-1 text-sm text-destructive">{error}</p>
          <button
            type="button"
            onClick={() => setReloadToken((t) => t + 1)}
            className="rounded-lg border border-destructive/40 px-3 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
          >
            Try again
          </button>
        </div>
      )}

      {/* ── Desktop table (§5.2 — exactly five columns) ───────────────────── */}
      <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <caption className="sr-only">Platform staff accounts. {visible.length} shown.</caption>
            <thead className="bg-muted/30">
              <tr>
                {COLUMNS.map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <SkeletonRows />
              ) : showEmpty || showNoResults || error ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-4 py-12 text-center">
                    <FontAwesomeIcon icon={faUsers} className="mb-2 h-6 w-6 opacity-30" />
                    <p className="text-sm text-muted-foreground">
                      {error
                        ? 'Platform users could not be loaded.'
                        : showEmpty
                          ? 'No platform users found.'
                          : 'No users match these filters.'}
                    </p>
                    {showNoResults && (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="mt-3 text-sm font-medium text-primary hover:underline"
                      >
                        Clear filters
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                visible.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => setSelectedId(user.id)}
                    className="cursor-pointer transition-colors hover:bg-muted/20"
                  >
                    <td className="px-4 py-3">
                      {/* One identity cell — name, email, and username when
                          present. The raw id is never rendered (§5.2). */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedId(user.id);
                        }}
                        className="text-left focus:outline-none focus:ring-2 focus:ring-ring"
                        aria-label={`View details for ${user.displayName}`}
                      >
                        <p className="font-medium text-foreground">{user.displayName}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                        {user.username !== null && (
                          <p className="text-xs text-muted-foreground">{user.username}</p>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <RoleBadge role={user.role} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge isActive={user.isActive} />
                    </td>
                    <td
                      className="whitespace-nowrap px-4 py-3 text-muted-foreground"
                      title={user.lastLoginAt ? formatUserDateTime(user.lastLoginAt) : undefined}
                    >
                      {formatLastSignIn(user.lastLoginAt)}
                    </td>
                    <td
                      className="whitespace-nowrap px-4 py-3 text-muted-foreground"
                      title={formatUserDateTime(user.createdAt)}
                    >
                      {formatRelative(user.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Narrow cards (§5.6) ───────────────────────────────────────────────
          A five-column table cannot survive 320px, so below `md` the same rows
          become a semantic list of cards with labels, since the headers are
          gone. The whole card is the touch target, comfortably over 44px. */}
      <ul className="grid list-none grid-cols-1 gap-3 md:hidden">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <li key={i} className="animate-pulse rounded-xl border border-border bg-card p-4">
              <div className="h-3 w-2/3 rounded bg-muted" />
              <div className="mt-3 h-3 w-1/3 rounded bg-muted" />
            </li>
          ))
        ) : showEmpty || showNoResults || error ? (
          <li className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-10 text-center">
            <p className="text-sm text-muted-foreground">
              {error
                ? 'Platform users could not be loaded.'
                : showEmpty
                  ? 'No platform users found.'
                  : 'No users match these filters.'}
            </p>
            {showNoResults && (
              <button
                type="button"
                onClick={clearFilters}
                className="mt-3 text-sm font-medium text-primary hover:underline"
              >
                Clear filters
              </button>
            )}
          </li>
        ) : (
          visible.map((user) => (
            <li key={user.id}>
              <button
                type="button"
                onClick={() => setSelectedId(user.id)}
                aria-label={`View details for ${user.displayName}`}
                className="w-full rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-muted/20 focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{user.displayName}</p>
                    <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                    {user.username !== null && (
                      <p className="truncate text-xs text-muted-foreground">{user.username}</p>
                    )}
                  </div>
                  <StatusBadge isActive={user.isActive} />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <RoleBadge role={user.role} />
                  <span className="text-xs text-muted-foreground">
                    Last sign-in: {formatLastSignIn(user.lastLoginAt)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Created: {formatRelative(user.createdAt)}
                  </span>
                </div>
              </button>
            </li>
          ))
        )}
      </ul>

      {/* ── Role reference card (§C, OD-C) ────────────────────────────────────
          Derived from the matrix, with impersonation excluded by name. Visible
          to every role that can reach this page: understanding who can do what
          is a reading task, not a management one. */}
      <PlatformRoleReferenceCard />

      {/* ── Detail drawer (§5.4) — component state, not a route ───────────── */}
      {selected && (
        <PlatformUserDetailDrawer
          user={selected}
          isSelf={isSelfSelected}
          canManage={canManage}
          onClose={() => {
            setSelectedId(null);
            closeDialogs();
          }}
          onAction={(next) => {
            setActionError('');
            setAction(next);
          }}
          onEditIdentity={() => {
            setActionError('');
            setEditingIdentity(true);
          }}
        />
      )}

      {/* Creation is not tied to a selected row, so it is gated on `canManage`
          alone — the same second gate, applied to the one mutation that has no
          target user. */}
      {canManage && (
        <PlatformUserCreateDialog
          open={creating}
          submitting={submitting}
          error={actionError}
          onClose={closeDialogs}
          onSubmit={(payload) => void createUser(payload)}
        />
      )}

      {/* Dialogs render only when a user is selected AND the operator may
          manage — a second gate behind the drawer's own, so a stale state
          transition cannot surface a mutating form to a read-only role. */}
      {selected && canManage && (
        <>
          <PlatformUserActionDialog
            action={action}
            user={selected}
            isSelf={isSelfSelected}
            submitting={submitting}
            error={actionError}
            onClose={closeDialogs}
            onConfirm={(role) => void runAction(role)}
          />
          <PlatformUserIdentityDialog
            open={editingIdentity}
            user={selected}
            isSelf={isSelfSelected}
            submitting={submitting}
            error={actionError}
            onClose={closeDialogs}
            onSubmit={(payload) => void saveIdentity(payload)}
          />
        </>
      )}
    </div>
  );
}

/**
 * Skeleton rows — §5.5 mandates skeletons, not a spinner: the page keeps its
 * shape while loading, so nothing jumps when the data arrives.
 */
function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <tr key={i} className="animate-pulse">
          {COLUMNS.map((column) => (
            <td key={column} className="px-4 py-4">
              <div className="h-3 w-24 rounded bg-muted" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
